import {
  checkGatedAccess,
  ResourceData,
  ungateResource,
} from "./gated-resources"
import {
  setDefaultHiddenFields,
  setResourceHiddenFields,
} from "./hidden-fields"
import analyticsAction from "../analytics/analytics-action"
import analyticsError from "../analytics/analytics-error"
import analyticsFormSubmit from "../analytics/analytics-form-submit"
declare global {
  interface Window {
    hbspt: {
      forms: {
        create: (setup: unknown) => void
      }
    }
  }
}

interface Props {
  formId: string
  formInstanceId: string
  errorMessageClass: string
  cssClass: string
  submitButtonClass: string
  pageTheme: string
  resourceData?: ResourceData // Optional data for gated content
}

async function hubspotForms(
  {},
  {
    formId,
    formInstanceId,
    errorMessageClass,
    cssClass,
    submitButtonClass,
    pageTheme,
    resourceData, // Optional URL for gated content
  }: Props
) {
  try {
    // Do not load the form if gated access has already been granted
    if (resourceData && checkGatedAccess(resourceData)) {
      return
    }

    if (!window.hbspt) {
      throw new Error("Hubspot Forms not loaded.")
    }

    window.hbspt.forms.create({
      portalId: "516278",
      formId: formId,

      // If same form appears >1 per page. As unique formInstanceId is required
      formInstanceId: formInstanceId,
      target: `#hsform-${formInstanceId}`,

      // Removes Hubspot CSS
      css: "",
      cssRequired: "",

      // Additional input error message class eg. colour
      errorMessageClass: errorMessageClass,

      // Additional subscribe form classes added to <form> element
      cssClass: `hubspot-form ${cssClass}`,

      // Add custom button classes
      submitButtonClass: submitButtonClass,

      onFormReady: function ($form: HTMLFormElement) {
        // Handle whether form is visible or in a hidden dialog
        const isVisible = $form.checkVisibility()

        if (isVisible) {
          analyticsAction("Form loaded", {
            "Form ID": formId,
            "Form provider": "hubspot",
          })
        } else {
          const formDialog = document.getElementById(`dialog-for-${formId}`)
          formDialog?.addEventListener(
            "formDialogOpened",
            e => {
              if ((e as CustomEvent).detail === `dialog-for-${formId}`) {
                analyticsAction("Form loaded", {
                  "Form ID": formId,
                  "Form provider": "hubspot",
                })
              }
            },
            { once: true }
          )
        }

        // Set default hidden fields
        setDefaultHiddenFields($form, formInstanceId, pageTheme)

        // Set resource-specific hidden fields
        if (resourceData) {
          setResourceHiddenFields($form, resourceData)
        }
      },
      onFormSubmitted: function (
        _$form: HTMLFormElement,
        data: {
          redirectUrl?: string
          submissionValues: Record<string, string>
        }
      ) {
        // Ensure the default_cta field is set
        if (!data.submissionValues.default_cta) {
          const isDefaultCTAForm = formInstanceId === "default-form"
          data.submissionValues.default_cta = isDefaultCTAForm
            ? "true"
            : "false"
        }

        analyticsFormSubmit(formId, data)

        // Site-wide modal handling
        const modalParent = _$form.closest("dialog")
        if (modalParent && !data.redirectUrl) {
          const dialogCopies = modalParent.querySelectorAll(".dialog-copy")
          const dialogForm = modalParent.querySelector(".dialog-form")
          const submittedMessage =
            modalParent.querySelector(".submitted-message")
          dialogCopies.forEach(copy => {
            copy.classList.add("hidden")
          })
          dialogForm?.classList.remove("desktop:col-span-3")
          dialogForm?.classList.add("desktop:col-span-full", "text-center")
          submittedMessage?.classList.add(
            "text-center",
            "[&>p:first-child]:font-heading",
            "[&>p:first-child]:font-medium",
            "[&>p:first-child]:heading-xs",
            "[&>p:first-child]:mb-24"
          )
        }

        if (resourceData) {
          ungateResource(resourceData.url)
        }
      },
    })
  } catch (error) {
    if (error instanceof Error) {
      await analyticsError(`Hubspot form`, error.message)
    }
  }
}

export default hubspotForms
